import { useEffect, useMemo, useState } from 'react';

import { generateGetSignedUrl, listS3Objects } from 'api/action';
import { ActionResponse, ResponseAttachment } from 'interfaces';

import * as Sentry from '@sentry/react';

const CLAIM_ATTACHMENT_BUCKET = `claim-attachments-${
  import.meta.env.VITE_ACCOUNT_ID
}`;

export type ResponseAttachmentsByID = Record<string, ResponseAttachment[]>;

const fetchResponseAttachments = async (
  claimID: string,
  responseID: string
): Promise<{ responseID: string; attachments: ResponseAttachment[] }> => {
  const prefix = `claims/${claimID}/actionResponses/${responseID}/`;

  try {
    // 1) List S3 objects under the response prefix.
    const keys = await listS3Objects({
      bucket: CLAIM_ATTACHMENT_BUCKET,
      prefix,
    });

    // 2) Ignore empty keys and folder placeholders.
    const filteredKeys = keys.filter(
      (key) => key && key !== prefix && !key.endsWith('/')
    );

    // 3) Convert each key to a signed URL
    const attachments: ResponseAttachment[] = await Promise.all(
      filteredKeys.map(async (key) => {
        const url = await generateGetSignedUrl({
          bucket: CLAIM_ATTACHMENT_BUCKET,
          key,
        });
        return {
          key,
          url,
          filename: key.split('/').pop() ?? key,
        };
      })
    );

    return { responseID, attachments };
  } catch (error) {
    // Keep the chain usable even if one response fails.
    Sentry.captureException(error);
    // console.error('listS3Objects failed', { responseID, error });
    return { responseID, attachments: [] };
  }
};

export const useFetchResponseAttachments = (
  isOpen: boolean,
  claimID?: string,
  responses?: ActionResponse[]
) => {
  const [attachmentsByResponseID, setAttachmentsByResponseID] =
    useState<ResponseAttachmentsByID>({});

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Normalize response IDs to a stable, deduped list so the effect only runs
  // when the actual set of responses changes.
  const responseIDs = useMemo(() => {
    if (!responses?.length) return [];
    return Array.from(new Set(responses.map((r) => r.id))).sort();
  }, [responses]);

  useEffect(() => {
    // Clear state when modal is closed or inputs are missing.
    if (!isOpen || !claimID || responseIDs.length === 0) {
      setAttachmentsByResponseID((prev) =>
        Object.keys(prev).length ? {} : prev
      );
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;

    const fetchResponseDocuments = async () => {
      setLoading(true);
      setError(null);

      try {
        const results = await Promise.all(
          responseIDs.map((responseID) =>
            fetchResponseAttachments(claimID, responseID)
          )
        );

        if (cancelled) return;

        const next: ResponseAttachmentsByID = {};
        for (const result of results) {
          next[result.responseID] = result.attachments;
        }

        setAttachmentsByResponseID(next);
      } catch (error) {
        if (!cancelled) {
          setError('Failed to load attachments. Please try again later.');
          Sentry.captureException(error);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchResponseDocuments();

    return () => {
      cancelled = true;
    };
  }, [isOpen, claimID, responseIDs]);

  return { attachmentsByResponseID, loading, error };
};
